import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, withStyles } from '@material-ui/core';
import moment from 'moment';
import styles from './styles';
import { usersAnalyticRequested } from '../../analytics/actions';
import {
    getTopUsers,
    getTopUsersByActions,
    isActivityStatsLoading,
} from '../../analytics/selectors';
import { analyticsUsersRequested } from '../../challenges/actions';
import {
    getAnalyticsUsersLoading,
    getTopUsers as getChallengeTopUsers,
    getTopUsersByActions as getChallengeTopUsersByActions,
} from '../../challenges/selectors';
import { getActiveCommunityId } from '../../user/selectors';
import Leaderboard from '../../common/Leaderboard';
import RequestLoader from '../../common/RequestLoader';
import DatePicker from '../../common/DatePicker';
import Button from '../../common/Button';
import TopSection from '../../common/ViewAll/TopSection';
import Select, { SIZE_LARGE } from '../../common/Select';
import { DATE_FORMAT } from '../AnalyticsScreen';
import queryString from 'query-string';
import { getAppImages } from '../../utils';
import { SORT_METHODS } from './constants';

const TopActionsScreen = ({
    classes,
    communityId,
    isLoading,
    topUsers,
    topUsersByActions,
    fetchTopUsers,
    challengeUsers,
    challengeUsersByActions,
    fetchChallengeUsers,
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();

    const queryVals = queryString.parse(location.search);
    const sortingOptions = [
        { id: SORT_METHODS.POINTS, label: t('sort.methods.points') },
        {
            id: SORT_METHODS.NUM_ACTIONS_LOGGED,
            label: t('sort.methods.numberOfActionsLogged'),
        },
    ];

    const [startDate, setStartDate] = useState(
        queryVals.startDate
            ? moment(queryVals.startDate)
            : moment().subtract(1, 'months'),
    );
    const [endDate, setEndDate] = useState(
        queryVals.endDate ? moment(queryVals.endDate) : moment(),
    );

    const [sortingOption, setSortingOption] = useState(sortingOptions[0].id);

    const loadUsers = () => {
        const analyticsOptions = {
            startDateIso: startDate.toISOString(),
            endDateIso: endDate.toISOString(),
            scope: 'organization',
            sortingOption,
        };

        const challengeOptions = {
            communityId,
            challengeId: queryVals.challengeId,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            summaryType: 'users',
            sortingOption,
        };

        if (queryVals.challengeId) {
            fetchChallengeUsers(challengeOptions);
        } else {
            fetchTopUsers(analyticsOptions, communityId);
        }
    };

    const handleSubmit = () => {
        loadUsers();
    };

    const handleSortingOptionChange = useCallback(
        (event) => {
            setSortingOption(event.target.value);
        },
        [setSortingOption],
    );

    useEffect(() => {
        loadUsers();
        // We want this to run only once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortingOption]);

    const users = useMemo(() => {
        if (sortingOption === SORT_METHODS.POINTS) {
            return queryVals.challengeId ? challengeUsers : topUsers;
        } else if (sortingOption === SORT_METHODS.NUM_ACTIONS_LOGGED) {
            return queryVals.challengeId
                ? challengeUsersByActions
                : topUsersByActions;
        }
    }, [
        queryVals,
        challengeUsers,
        topUsers,
        challengeUsersByActions,
        topUsersByActions,
        sortingOption,
    ]);

    return (
        <Grid container direction="column" className={classes.container}>
            <Grid item>
                <TopSection
                    title={t('topUsers.title')}
                    handleBack={history.goBack}
                    intercomTargetPrefix="topUsers"
                />
            </Grid>
            <Grid item>
                <DatePicker
                    minDate={queryVals.challengeId && startDate}
                    maxDate={endDate}
                    format={DATE_FORMAT}
                    value={startDate}
                    onChange={setStartDate}
                />
            </Grid>
            <Grid item style={{ marginTop: '10px' }}>
                <DatePicker
                    minDate={startDate}
                    format={DATE_FORMAT}
                    value={endDate}
                    onChange={setEndDate}
                />
            </Grid>
            <Grid item>
                <Button
                    className={classes.submitButton}
                    color="accent"
                    onClick={handleSubmit}
                >
                    {t('Submit')}
                </Button>
            </Grid>
            <Grid
                item
                container
                direction="column"
                style={{ marginTop: '10px' }}
            >
                <Grid item>
                    <span className={classes.subtitle}>{t('sort.select')}</span>
                </Grid>
                <Grid item>
                    <Select
                        type={SIZE_LARGE}
                        options={sortingOptions.map((opt) => ({
                            value: opt.id,
                            label: opt.label,
                        }))}
                        value={sortingOption}
                        onChange={handleSortingOptionChange}
                        disabled={isLoading}
                    />
                </Grid>
            </Grid>
            <hr className={classes.divider} />
            {isLoading ? (
                <Grid container item className={classes.loaderWrapper}>
                    <RequestLoader
                        isLoading={isLoading}
                        title={t('Loading Members...')}
                    />
                </Grid>
            ) : users.length !== 0 ? (
                <Leaderboard
                    leaderboardCardProps={{
                        defaultImageSrc: getAppImages().defaultProfileIcon,
                        imageIsRounded: true,
                    }}
                    items={users}
                />
            ) : (
                <Typography className={classes.emptyText}>
                    {t('No members were found.')}
                    <br />
                    {t('Try expanding the time range.')}
                </Typography>
            )}
        </Grid>
    );
};

TopActionsScreen.propTypes = {
    classes: PropTypes.object.isRequired,
    communityId: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    topUsers: PropTypes.array.isRequired,
    topUsersByActions: PropTypes.array.isRequired,
    fetchTopUsers: PropTypes.func.isRequired,
    challengeUsers: PropTypes.array.isRequired,
    challengeUsersByActions: PropTypes.array.isRequired,
    fetchChallengeUsers: PropTypes.func.isRequired,
};

const StyledTopActionsScreen = withStyles(styles)(TopActionsScreen);

const mapDispatchToProps = (dispatch) => ({
    fetchTopUsers: (options, communityId) =>
        dispatch(usersAnalyticRequested(options, communityId)),
    fetchChallengeUsers: (args) => dispatch(analyticsUsersRequested(args)),
});

const mapStateToProps = (state) => ({
    communityId: getActiveCommunityId(state),
    isLoading: isActivityStatsLoading(state) || getAnalyticsUsersLoading(state),
    topUsers: getTopUsers(state),
    topUsersByActions: getTopUsersByActions(state),
    challengeUsers: getChallengeTopUsers(state),
    challengeUsersByActions: getChallengeTopUsersByActions(state),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(StyledTopActionsScreen);
